import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Calendar,
  ScrollText,
  Users,
  TrendingUp,
  Medal,
  MapPin,
  MessageSquare,
  CheckCircle2,
  Crown,
  Flame,
  Fingerprint,
  GraduationCap,
  Timer,
  ExternalLink,
  ClipboardList,
  BarChart3,
  Loader2,
  Info
} from 'lucide-react';
import SurveyContent from './SurveyContent';
import AdminResults from './AdminResults';
import RepsContent from './RepsContent';

// Progress lives in the browser: there are no user accounts, so points follow
// the device rather than a person. Every access is guarded — private-mode and
// storage-blocked browsers throw on read/write.
const STORAGE_KEY = 'civicspark.progress.v1';

const loadProgress = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { points: 0, answered: [] };
    const parsed = JSON.parse(raw);
    return {
      points: Number(parsed.points) || 0,
      answered: Array.isArray(parsed.answered) ? parsed.answered : []
    };
  } catch {
    return { points: 0, answered: [] };
  }
};

const saveProgress = (points, answered) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ points, answered }));
  } catch {
    /* storage unavailable — progress simply does not persist */
  }
};

const levelFor = points => Math.floor(points / 500) + 1;

// The admin results view is reachable only at ?admin — it is password-gated
// server-side regardless, this just keeps it out of the student-facing nav.
const adminRequested = () => {
  try {
    return new URLSearchParams(window.location.search).has('admin');
  } catch {
    return false;
  }
};

// ?tab=survey lets another site link straight to a section instead of dropping
// the visitor on the dashboard. Unknown values fall back rather than blanking.
const TABS = ['dashboard', 'bills', 'reps', 'elections', 'volunteer', 'tracker', 'profile', 'survey'];

const requestedTab = () => {
  try {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.includes(t) ? t : 'dashboard';
  } catch {
    return 'dashboard';
  }
};

const App = () => {
  const initialProgress = React.useMemo(loadProgress, []);
  const showAdmin = React.useMemo(adminRequested, []);

  const [activeTab, setActiveTab] = useState(React.useMemo(requestedTab, []));
  const [userPoints, setUserPoints] = useState(initialProgress.points);
  const [selectedQuizAnswer, setSelectedQuizAnswer] = useState(null);
  const [showQuizResult, setShowQuizResult] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(initialProgress.answered);
  const [sessionPoints, setSessionPoints] = useState(0);

  const userLevel = levelFor(userPoints);

  // Tab navigation from anywhere in the page.
  const go = tab => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Live legislation. Fetched once here rather than inside BillsContent, which
  // is re-created on every render of App and would re-fire the request forever.
  const [bills, setBills] = useState(null);
  const [billsState, setBillsState] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/bills')
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.source === 'congress.gov' && d.bills.length) {
          setBills(d.bills);
          setBillsState('live');
        } else {
          setBillsState(d.source === 'unavailable' ? 'unconfigured' : 'error');
        }
      })
      .catch(() => { if (!cancelled) setBillsState('error'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    saveProgress(userPoints, answeredQuestions);
  }, [userPoints, answeredQuestions]);

  // Expanded quiz questions
  const quizQuestions = [
    {
      question: "How many senators does each state have in the U.S. Senate?",
      options: ["1", "2", "Depends on population", "4"],
      correct: 1,
      explanation: "Each state has exactly 2 senators, regardless of population size. This ensures equal representation of states in the Senate.",
      points: 50
    },
    {
      question: "How many representatives does each state have in the U.S. House of Representatives?",
      options: ["1", "2", "Depends on population", "4"],
      correct: 2,
      explanation: "The number of representatives each state has depends on its population. States are reapportioned every 10 years based on the census. For example, California has 52 representatives, while Wyoming has 1.",
      points: 50
    },
    {
      question: "What is the term length for a U.S. Senator?",
      options: ["1 year", "3 years", "6 years", "No term limit"],
      correct: 2,
      explanation: "U.S. Senators serve 6-year terms. There is no limit on the number of terms they can serve, but each term lasts 6 years.",
      points: 50
    },
    {
      question: "What is the term length for a U.S. House Representative?",
      options: ["1 year", "2 years", "6 years", "No term limit"],
      correct: 1,
      explanation: "U.S. House Representatives serve 2-year terms. This means they face reelection more frequently than Senators, making them more responsive to their constituents.",
      points: 50
    },
    {
      question: "How many voting members are in the U.S. House of Representatives?",
      options: ["100", "435", "538", "50"],
      correct: 1,
      explanation: "The House has 435 voting members, a number fixed by law since 1929. The 538 figure you may have seen is the Electoral College: 435 representatives + 100 senators + 3 electors for Washington, D.C.",
      points: 50
    },
    {
      question: "How many electoral votes are needed to win the presidency?",
      options: ["218", "270", "435", "538"],
      correct: 1,
      explanation: "270 — a simple majority of the 538 total electoral votes. If no candidate reaches 270, the House chooses the president, with each state delegation casting one vote.",
      points: 50
    },
    {
      question: "Which constitutional amendment lowered the voting age to 18?",
      options: ["The 19th", "The 21st", "The 24th", "The 26th"],
      correct: 3,
      explanation: "The 26th Amendment, ratified in 1971 during the Vietnam War, lowered the voting age from 21 to 18. It was driven largely by the argument that people old enough to be drafted should be old enough to vote.",
      points: 50
    },
    {
      question: "Who has the power to declare war?",
      options: ["The President", "Congress", "The Supreme Court", "The Secretary of Defense"],
      correct: 1,
      explanation: "Article I of the Constitution gives Congress the power to declare war. The President is Commander in Chief and directs the military, which is why the two branches often disagree over the use of force.",
      points: 50
    },
    {
      question: "How many justices serve on the U.S. Supreme Court?",
      options: ["7", "9", "11", "13"],
      correct: 1,
      explanation: "Nine — one Chief Justice and eight Associate Justices. The number is set by Congress, not the Constitution, and has changed several times in U.S. history.",
      points: 50
    },
    {
      question: "What share of both chambers of Congress is needed to override a presidential veto?",
      options: ["A simple majority", "Three-fifths", "Two-thirds", "Three-fourths"],
      correct: 2,
      explanation: "A two-thirds vote in both the House and the Senate. That is a high bar, which is why most vetoes are never overridden.",
      points: 50
    },
    {
      question: "How often is the U.S. census conducted?",
      options: ["Every 2 years", "Every 4 years", "Every 10 years", "Every 20 years"],
      correct: 2,
      explanation: "Every 10 years, as required by the Constitution. The count determines how many House seats each state gets and guides the distribution of federal funding.",
      points: 50
    }
  ];

  const dailyChallenge = quizQuestions[currentQuestionIndex];

  const recentBills = [
    {
      id: 1,
      title: "Student Loan Forgiveness Act",
      summary: "Proposes to forgive up to $10,000 in federal student loan debt for borrowers earning less than $125,000 annually.",
      status: "In Committee",
      impact: "High",
      simplified: "This bill would help cancel some student debt for middle-class borrowers."
    },
    {
      id: 2,
      title: "Clean Energy Investment Act",
      summary: "Allocates $50 billion for renewable energy infrastructure and green job training programs.",
      status: "Passed House",
      impact: "Medium",
      simplified: "This bill invests money in solar, wind, and other clean energy jobs."
    }
  ];

  const upcomingElections = [
    { date: "Nov 5, 2025", type: "City Council", location: "Orlando, FL", daysUntil: 80 },
    { date: "Mar 15, 2026", type: "School Board", location: "Orange County, FL", daysUntil: 210 }
  ];

  const volunteerOpportunities = [
    {
      id: 1,
      title: "Food Bank Volunteer",
      organization: "Second Harvest Food Bank",
      date: "This Saturday",
      location: "Downtown Orlando",
      points: 100,
      category: "Community Service",
      url: "https://www.feedhopenow.org/volunteer"
    },
    {
      id: 2,
      title: "Voter Registration Drive",
      organization: "League of Women Voters",
      date: "Next Tuesday",
      location: "UCF Campus",
      points: 150,
      category: "Civic Action",
      url: null
    },
    {
      id: 3,
      title: "Environmental Cleanup",
      organization: "Keep Orlando Beautiful",
      date: "Sunday Morning",
      location: "Lake Eola Park",
      points: 75,
      category: "Environment",
      url: null
    }
  ];

  const actionHistory = [
    { action: "Called Rep. Darren Soto", date: "2 days ago", points: 100, impact: "Your call was logged as constituent feedback" },
    { action: "Attended Town Hall", date: "1 week ago", points: 200, impact: "1 of 150 attendees - strong turnout!" },
    { action: "Completed Daily Challenge", date: "3 days ago", points: 50, impact: "Learned about the Electoral College" }
  ];

  const badges = [
    { name: "First Steps", description: "Completed your first civic action", earned: true, icon: "🚀" },
    { name: "Voice Heard", description: "Contacted your representative", earned: true, icon: "📢" },
    { name: "Community Helper", description: "Volunteered 5+ hours", earned: true, icon: "🤝" },
    { name: "Knowledge Seeker", description: "Completed 10 daily challenges", earned: false, icon: "🧠" },
    { name: "Democracy Champion", description: "Reached 2000 points", earned: false, icon: "🏆" }
  ];

  const handleQuizSubmit = () => {
    if (selectedQuizAnswer !== null) {
      setShowQuizResult(true);
      const isCorrect = selectedQuizAnswer === dailyChallenge.correct;
      if (isCorrect) {
        const pointsEarned = dailyChallenge.points;
        setUserPoints(prev => prev + pointsEarned);
        setSessionPoints(prev => prev + pointsEarned);
      }
      setAnsweredQuestions(prev => [...prev, currentQuestionIndex]);
    }
  };

  const handleNextQuestion = () => {
    // Find next unanswered question
    const nextIndex = (currentQuestionIndex + 1) % quizQuestions.length;
    
    setCurrentQuestionIndex(nextIndex);
    setSelectedQuizAnswer(null);
    setShowQuizResult(false);
  };

  const hasMoreQuestions = answeredQuestions.length < quizQuestions.length;
  const allQuestionsAnswered = answeredQuestions.length >= quizQuestions.length;

  const PointsBadge = () => {
    return (
      <div className="bg-indigo-600 text-white px-5 py-2.5 rounded-2xl flex items-center space-x-2.5 shadow-lg backdrop-blur-sm">
        <Flame size={18} className="animate-pulse" />
        <span className="font-semibold text-sm">{userPoints} points</span>
        <div className="bg-white/20 px-2 py-0.5 rounded-lg">
          <span className="text-xs font-medium">Level {userLevel}</span>
        </div>
      </div>
    );
  };

  const TabButton = ({ id, label, icon: Icon, isActive, onClick }) => {
    return (
      <button
        onClick={onClick}
        className={`flex items-center space-x-2 px-4 py-2.5 rounded-2xl transition-all duration-200 ${
          isActive 
            ? 'bg-blue-600 text-white shadow-md transform scale-105' 
            : 'bg-white/60 text-gray-700 hover:bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md'
        }`}
      >
        <Icon size={18} />
        <span className="font-medium text-sm">{label}</span>
      </button>
    );
  };

  const DashboardContent = () => {
    return (
      <div className="space-y-8">
        <div className="bg-blue-600 text-white p-8 rounded-3xl shadow-xl backdrop-blur-sm">
          <h2 className="text-3xl font-bold mb-3">Welcome back! 🇺🇸</h2>
          <p className="opacity-90 text-lg">Ready to spark democracy in your community?</p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-500 rounded-2xl">
                <GraduationCap className="text-white" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Daily Knowledge Challenge</h3>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-600">
                Question {currentQuestionIndex + 1} of {quizQuestions.length}
              </span>
              {sessionPoints > 0 && (
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                  <Flame size={14} />
                  <span>+{sessionPoints} today</span>
                </div>
              )}
            </div>
          </div>
          
          {!showQuizResult ? (
            <div>
              <p className="mb-6 text-gray-700 text-lg leading-relaxed">{dailyChallenge.question}</p>
              <div className="space-y-3 mb-6">
                {dailyChallenge.options.map((option, index) => (
                  <label key={index} className="flex items-center space-x-3 cursor-pointer p-4 rounded-2xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all">
                    <input
                      type="radio"
                      name={`quiz-${currentQuestionIndex}`}
                      value={index}
                      checked={selectedQuizAnswer === index}
                      onChange={() => setSelectedQuizAnswer(index)}
                      className="text-blue-600 w-4 h-4"
                    />
                    <span className="text-gray-800 font-medium">{option}</span>
                  </label>
                ))}
              </div>
              <button
                onClick={handleQuizSubmit}
                disabled={selectedQuizAnswer === null}
                className="bg-blue-600 text-white px-6 py-3 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transform hover:scale-105 transition-all font-semibold"
              >
                Submit Answer • +{dailyChallenge.points} points
              </button>
            </div>
          ) : allQuestionsAnswered ? (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border-2 border-green-200">
              <p className="text-green-800 font-bold text-lg mb-2 flex items-center space-x-2">
                <Crown size={24} />
                <span>All Questions Completed! 🎓</span>
              </p>
              <p className="text-green-700">
                You've answered all {quizQuestions.length} questions and earned {sessionPoints} points today. Come back tomorrow for more challenges!
              </p>
            </div>
          ) : (
            <div>
              <div className={`p-6 rounded-2xl mb-4 ${selectedQuizAnswer === dailyChallenge.correct ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                <p className={`font-bold text-lg mb-3 ${selectedQuizAnswer === dailyChallenge.correct ? 'text-green-800' : 'text-red-800'}`}>
                  {selectedQuizAnswer === dailyChallenge.correct ? '🎉 Correct!' : '💡 Not quite!'}
                </p>
                <p className="text-gray-700 mb-3 leading-relaxed">{dailyChallenge.explanation}</p>
                {selectedQuizAnswer === dailyChallenge.correct && (
                  <p className="text-green-700 font-semibold flex items-center space-x-2">
                    <Flame size={18} />
                    <span>You earned {dailyChallenge.points} points!</span>
                  </p>
                )}
              </div>
              
              <button
                onClick={handleNextQuestion}
                className="bg-blue-600 text-white px-6 py-3 rounded-2xl hover:bg-blue-700 transform hover:scale-105 transition-all font-semibold"
              >
                Next Question →
              </button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            { tab: 'reps',      icon: MessageSquare, tone: 'bg-blue-500',  title: 'Contact Reps', copy: 'Connect with your representatives about the issues you care about most.' },
            { tab: 'volunteer', icon: Users,         tone: 'bg-red-500',   title: 'Volunteer',    copy: 'Discover meaningful opportunities to make a difference in your community.' },
            { tab: 'bills',     icon: ScrollText,    tone: 'bg-green-500', title: 'Track Bills',  copy: 'Stay informed about legislation that directly impacts your life.' }
          ].map(card => {
            const Icon = card.icon;
            return (
              <button
                key={card.tab}
                onClick={() => go(card.tab)}
                className="text-left bg-white/90 backdrop-blur-sm p-6 rounded-3xl shadow-lg border border-gray-100/50 cursor-pointer hover:shadow-xl hover:transform hover:scale-105 transition-all duration-200"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`p-3 ${card.tone} rounded-2xl`}>
                    <Icon className="text-white" size={24} />
                  </div>
                  <h4 className="font-bold text-lg text-gray-900">{card.title}</h4>
                </div>
                <p className="text-gray-600 leading-relaxed">{card.copy}</p>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Placeholder content is labelled rather than left to read as real.
  const SampleNotice = ({ children }) => (
    <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-2xl flex items-start gap-3">
      <Info size={20} className="shrink-0 mt-0.5" />
      <p className="text-sm leading-relaxed">{children}</p>
    </div>
  );

  const BillsContent = () => {
    if (billsState === 'live') {
      return (
        <div className="space-y-8">
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-2 bg-indigo-600 rounded-2xl">
              <ScrollText className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Bill Tracker</h2>
              <p className="text-sm text-gray-500 font-medium">Live from congress.gov</p>
            </div>
          </div>

          {bills.map(bill => (
            <div key={bill.id} className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50 hover:shadow-xl transition-all duration-200">
              <div className="flex justify-between items-start mb-4 gap-4">
                <h3 className="text-xl font-bold text-gray-900 leading-tight">{bill.title}</h3>
                <span className="px-4 py-2 rounded-2xl text-sm font-semibold bg-indigo-100 text-indigo-800 shrink-0">
                  {bill.number}
                </span>
              </div>
              <div className="bg-blue-50 p-4 rounded-2xl border-l-4 border-blue-400 mb-6">
                <p className="text-blue-800 text-sm leading-relaxed">
                  <strong>Latest action{bill.actionDate ? ` (${bill.actionDate})` : ''}:</strong> {bill.status}
                </p>
              </div>
              {bill.url && (
                <a
                  href={bill.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-2.5 rounded-2xl font-semibold hover:bg-blue-700 transition-all"
                >
                  <span>Read on congress.gov</span>
                  <ExternalLink size={16} />
                </a>
              )}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-2 bg-indigo-600 rounded-2xl">
            <ScrollText className="text-white" size={24} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Bill Tracker</h2>
        </div>

        {billsState === 'loading' ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="text-blue-600 animate-spin mb-3" size={32} />
            <p className="text-gray-500">Loading current legislation...</p>
          </div>
        ) : (
          <SampleNotice>
            <strong>These are sample bills, not live legislation.</strong>{' '}
            {billsState === 'unconfigured'
              ? 'Set the CONGRESS_API_KEY config var to pull real bills from congress.gov.'
              : 'The congress.gov feed could not be reached, so sample bills are shown instead.'}
          </SampleNotice>
        )}

        {recentBills.map(bill => (
          <div key={bill.id} className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50 hover:shadow-xl transition-all duration-200">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-900 leading-tight">{bill.title}</h3>
              <span className={`px-4 py-2 rounded-2xl text-sm font-semibold ${
                bill.status === 'Passed House' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-orange-800'
              }`}>
                {bill.status}
              </span>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4 leading-relaxed">{bill.summary}</p>
              <div className="bg-blue-50 p-4 rounded-2xl border-l-4 border-blue-400">
                <p className="text-blue-800"><strong>In Plain English:</strong> {bill.simplified}</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className={`px-3 py-1.5 rounded-2xl text-sm font-semibold ${
                bill.impact === 'High' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
              }`}>
                {bill.impact} Impact
              </span>
              <button
                onClick={() => go('reps')}
                className="bg-blue-600 text-white px-6 py-2.5 rounded-2xl font-semibold hover:bg-blue-700 transform hover:scale-105 transition-all"
              >
                Contact Rep About This →
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ElectionsContent = () => {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-2 bg-purple-600 rounded-2xl">
            <Calendar className="text-white" size={24} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Election Hub</h2>
        </div>

        <SampleNotice>
          <strong>These are sample elections.</strong> Dates and races are placeholders
          and are not checked against your local election office.
        </SampleNotice>
        
        {upcomingElections.map((election, index) => (
          <div key={index} className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50 hover:shadow-xl transition-all duration-200">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{election.type}</h3>
                <p className="text-gray-600 flex items-center space-x-2 text-lg">
                  <MapPin size={18} />
                  <span>{election.location}</span>
                </p>
              </div>
              <div className="text-right">
                <div className="bg-purple-600 text-white p-6 rounded-3xl text-center shadow-lg">
                  <p className="text-4xl font-bold">{election.daysUntil}</p>
                  <p className="text-purple-100 font-medium">days until</p>
                </div>
                <p className="text-sm text-gray-600 mt-3 font-medium">{election.date}</p>
              </div>
            </div>
            <div className="mt-6 flex space-x-4">
              <button className="bg-purple-600 text-white px-6 py-3 rounded-2xl font-semibold hover:bg-purple-700 transform hover:scale-105 transition-all">
                Set Reminder
              </button>
              <button className="bg-white/60 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-2xl font-semibold border border-gray-200 hover:bg-white/80 transition-all">
                View Candidates
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const VolunteerContent = () => {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-2 bg-red-500 rounded-2xl">
            <Users className="text-white" size={24} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Local Volunteer Board</h2>
        </div>

        <SampleNotice>
          <strong>These are sample listings.</strong> Dates and details are placeholders
          — confirm directly with the organization before showing up.
        </SampleNotice>
        
        {volunteerOpportunities.map(opportunity => (
          <div key={opportunity.id} className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50 hover:shadow-xl transition-all duration-200">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{opportunity.title}</h3>
                <p className="text-gray-600 font-medium">{opportunity.organization}</p>
              </div>
              <div className="bg-green-100 text-green-800 px-4 py-2 rounded-2xl font-semibold flex items-center space-x-1">
                <Flame size={16} />
                <span>+{opportunity.points} points</span>
              </div>
            </div>
            
            <div className="mb-6 space-y-3">
              <p className="text-gray-600 flex items-center space-x-3 text-lg">
                <Timer size={18} />
                <span>{opportunity.date}</span>
              </p>
              <p className="text-gray-600 flex items-center space-x-3 text-lg">
                <MapPin size={18} />
                <span>{opportunity.location}</span>
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-2xl text-sm font-semibold">
                {opportunity.category}
              </span>
              {opportunity.url ? (
                <a 
                  href={opportunity.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 text-white px-8 py-3 rounded-2xl font-semibold hover:bg-green-700 transform hover:scale-105 transition-all flex items-center space-x-2"
                >
                  <span>Sign Up</span>
                  <ExternalLink size={16} />
                </a>
              ) : (
                <button className="bg-green-600 text-white px-8 py-3 rounded-2xl font-semibold hover:bg-green-700 transform hover:scale-105 transition-all">
                  Sign Up
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ActionTrackerContent = () => {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-2 bg-orange-500 rounded-2xl">
            <TrendingUp className="text-white" size={24} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Action Tracker</h2>
        </div>
        
        <div className="bg-orange-500 text-white p-8 rounded-3xl shadow-xl backdrop-blur-sm">
          <h3 className="text-2xl font-bold mb-6">Your Impact This Month</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <p className="text-4xl font-bold mb-2">5</p>
              <p className="opacity-90 font-medium">Actions Taken</p>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <p className="text-4xl font-bold mb-2">350</p>
              <p className="opacity-90 font-medium">Points Earned</p>
            </div>
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-6">
              <p className="text-4xl font-bold mb-2">12</p>
              <p className="opacity-90 font-medium">People Reached</p>
            </div>
          </div>
        </div>
        
        <div className="space-y-6">
          <h3 className="text-2xl font-bold text-gray-900">Recent Actions</h3>
          {actionHistory.map((action, index) => (
            <div key={index} className="bg-white/90 backdrop-blur-sm p-6 rounded-3xl shadow-lg border border-gray-100/50">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-bold text-gray-900 text-lg">{action.action}</h4>
                <div className="flex items-center space-x-2 bg-orange-100 px-4 py-2 rounded-2xl">
                  <span className="text-orange-600 font-bold">+{action.points}</span>
                  <Flame className="text-orange-600" size={16} />
                </div>
              </div>
              <p className="text-gray-500 text-sm mb-3 font-medium">{action.date}</p>
              <p className="text-green-700 bg-green-50 p-4 rounded-2xl text-sm border border-green-100">
                <strong>Impact:</strong> {action.impact}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ProfileContent = () => {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-3 mb-8">
          <div className="p-2 bg-yellow-500 rounded-2xl">
            <Medal className="text-white" size={24} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Your Profile</h2>
        </div>
        
        <div className="bg-white/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg border border-gray-100/50">
          <div className="text-center mb-8">
            <div className="w-32 h-32 bg-blue-600 rounded-full mx-auto mb-6 flex items-center justify-center text-white text-3xl font-bold shadow-xl">
              <Fingerprint size={48} />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">Democracy Champion</h3>
            <p className="text-gray-600 text-lg font-medium mb-4">Level {userLevel} • {userPoints} points</p>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2 overflow-hidden">
              <div className="bg-blue-600 h-4 rounded-full shadow-inner transition-all duration-500" style={{width: '65%'}}></div>
            </div>
            <p className="text-sm text-gray-500 font-medium">750 points to Level {userLevel + 1}</p>
          </div>
          
          <div className="mb-8">
            <h4 className="font-bold text-2xl mb-6 text-gray-900">Achievements</h4>
            <div className="grid grid-cols-2 gap-4">
              {badges.map((badge, index) => (
                <div key={index} className={`p-6 rounded-3xl border transition-all duration-200 ${badge.earned ? 'bg-yellow-50 border-yellow-200 shadow-lg hover:shadow-xl transform hover:scale-105' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                  <div className="text-3xl mb-3">{badge.icon}</div>
                  <p className="font-bold text-lg text-gray-900 mb-2">{badge.name}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{badge.description}</p>
                  {badge.earned && (
                    <div className="mt-3">
                      <Crown className="text-yellow-500 w-5 h-5" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-100/50 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Sparkles className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">CivicSpark</h1>
              <p className="text-sm text-gray-500 font-medium">Democracy in Your Pocket</p>
            </div>
          </div>
          <PointsBadge />
        </div>
      </header>

      <nav className="bg-white/60 backdrop-blur-md border-b border-gray-100/50 sticky top-20 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex space-x-3 overflow-x-auto">
            <TabButton
              id="dashboard"
              label="Dashboard"
              icon={Sparkles}
              isActive={activeTab === 'dashboard'}
              onClick={() => setActiveTab('dashboard')}
            />
            <TabButton
              id="bills"
              label="Bills"
              icon={ScrollText}
              isActive={activeTab === 'bills'}
              onClick={() => setActiveTab('bills')}
            />
            <TabButton
              id="elections"
              label="Elections"
              icon={Calendar}
              isActive={activeTab === 'elections'}
              onClick={() => setActiveTab('elections')}
            />
            <TabButton
              id="reps"
              label="Contact Reps"
              icon={MessageSquare}
              isActive={activeTab === 'reps'}
              onClick={() => setActiveTab('reps')}
            />
            <TabButton
              id="volunteer"
              label="Volunteer"
              icon={Users}
              isActive={activeTab === 'volunteer'}
              onClick={() => setActiveTab('volunteer')}
            />
            <TabButton
              id="tracker"
              label="Action Tracker"
              icon={TrendingUp}
              isActive={activeTab === 'tracker'}
              onClick={() => setActiveTab('tracker')}
            />
            <TabButton
              id="profile"
              label="Profile"
              icon={Medal}
              isActive={activeTab === 'profile'}
              onClick={() => setActiveTab('profile')}
            />
            <TabButton
              id="survey"
              label="Survey"
              icon={ClipboardList}
              isActive={activeTab === 'survey'}
              onClick={() => setActiveTab('survey')}
            />
            {showAdmin && (
              <TabButton
                id="results"
                label="Results"
                icon={BarChart3}
                isActive={activeTab === 'results'}
                onClick={() => setActiveTab('results')}
              />
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && <DashboardContent />}
        {activeTab === 'bills' && <BillsContent />}
        {activeTab === 'reps' && <RepsContent />}
        {activeTab === 'elections' && <ElectionsContent />}
        {activeTab === 'volunteer' && <VolunteerContent />}
        {activeTab === 'tracker' && <ActionTrackerContent />}
        {activeTab === 'profile' && <ProfileContent />}
        {activeTab === 'survey' && <SurveyContent />}
        {activeTab === 'results' && showAdmin && <AdminResults />}
      </main>

      <footer className="bg-white/60 backdrop-blur-md border-t border-gray-100/50 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-8 text-center text-gray-500">
          <p className="font-medium">CivicSpark - Empowering the next generation of civic leaders</p>
          <p className="text-sm mt-2 opacity-75">Built for the Congressional App Challenge</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
